import { useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '@/store';
import {
  fetchMediaDetails,
  mediaKey,
  type MediaCacheStatus,
  type MediaKey,
} from '@/store/slices/mediaSlice';
import type { MediaDetails, MediaType } from '@/types/streaming';

export interface MediaRef {
  type: MediaType;
  id: number;
}

export interface HydrationState {
  status: MediaCacheStatus;
  data?: MediaDetails;
}

/**
 * For each unique (type, id) in `refs`, ensures a fetchMediaDetails dispatch
 * has been kicked off and returns a Map<MediaKey, HydrationState> reflecting
 * the current cache state for the requested keys.
 *
 * Dedupe + the thunk's own `condition` guard mean each ref is fetched at most
 * once across the app's lifetime (until an error allows a retry).
 */
export function useHydratedMedia(refs: MediaRef[]): Map<MediaKey, HydrationState> {
  const dispatch = useAppDispatch();
  const byKey = useAppSelector((s) => s.media.byKey);

  // Dedupe refs and pre-compute a stable signature for the effect dep array
  // so identity-only changes to `refs` don't re-fire the effect.
  const uniqueRefs = useMemo(() => {
    const seen = new Map<MediaKey, MediaRef>();
    for (const ref of refs) {
      const key = mediaKey(ref.type, ref.id);
      if (!seen.has(key)) seen.set(key, ref);
    }
    return Array.from(seen.values());
  }, [refs]);

  const keysSignature = uniqueRefs
    .map((r) => mediaKey(r.type, r.id))
    .sort()
    .join('|');

  useEffect(() => {
    for (const ref of uniqueRefs) {
      // The thunk's `condition` skips if already loading or ready, so this is
      // safe to dispatch eagerly without re-checking state here.
      void dispatch(fetchMediaDetails({ type: ref.type, id: ref.id }));
    }
    // keysSignature captures the dedupe-aware set; uniqueRefs identity
    // changes with refs identity but that's OK — the condition guard prevents
    // wasted requests.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keysSignature]);

  return useMemo(() => {
    const out = new Map<MediaKey, HydrationState>();
    for (const ref of uniqueRefs) {
      const key = mediaKey(ref.type, ref.id);
      const entry = byKey[key];
      if (entry) {
        out.set(key, { status: entry.status, data: entry.data });
      }
    }
    return out;
  }, [uniqueRefs, byKey]);
}
